import type Pusher from 'pusher-js';
import type { Channel } from 'pusher-js';
import type { RealtimeChannelRef, RealtimeChannelType } from './types';

interface ChannelRegistryEntry {
  channel: Channel;
  refCount: number;
  eventHandlers: Map<string, Map<symbol, (payload: unknown) => void>>;
}

const channelRegistry = new Map<string, ChannelRegistryEntry>();

const normalizeChannelName = (channelName: string, channelType: RealtimeChannelType): string => {
  if (channelName.startsWith('private-') || channelName.startsWith('presence-')) {
    return channelName;
  }

  if (channelType === 'private') {
    return `private-${channelName}`;
  }

  if (channelType === 'presence') {
    return `presence-${channelName}`;
  }

  return channelName;
};

export const acquireChannel = (
  client: Pusher,
  channelName: string,
  channelType: RealtimeChannelType = 'public'
): RealtimeChannelRef => {
  const normalizedName = normalizeChannelName(channelName, channelType);
  const existing = channelRegistry.get(normalizedName);

  if (existing) {
    existing.refCount += 1;
    return {
      channel: existing.channel,
      normalizedName,
    };
  }

  const channel = client.subscribe(normalizedName);
  channelRegistry.set(normalizedName, {
    channel,
    refCount: 1,
    eventHandlers: new Map(),
  });

  return {
    channel,
    normalizedName,
  };
};

export const releaseChannel = (client: Pusher, normalizedName: string): void => {
  const entry = channelRegistry.get(normalizedName);
  if (!entry) {
    return;
  }

  entry.refCount -= 1;
  if (entry.refCount > 0) {
    return;
  }

  entry.eventHandlers.forEach((handlersByToken, eventName) => {
    handlersByToken.forEach(handler => {
      entry.channel.unbind(eventName, handler);
    });
  });

  channelRegistry.delete(normalizedName);
  client.unsubscribe(normalizedName);
};

export const bindChannelEvent = (
  normalizedName: string,
  eventName: string,
  handler: (payload: unknown) => void
): symbol | null => {
  const entry = channelRegistry.get(normalizedName);
  if (!entry) {
    return null;
  }

  const token = Symbol(`${normalizedName}:${eventName}`);
  const handlersByEvent = entry.eventHandlers.get(eventName) || new Map<symbol, (payload: unknown) => void>();
  handlersByEvent.set(token, handler);
  entry.eventHandlers.set(eventName, handlersByEvent);
  entry.channel.bind(eventName, handler);

  return token;
};

export const unbindChannelEvent = (normalizedName: string, eventName: string, token: symbol): void => {
  const entry = channelRegistry.get(normalizedName);
  if (!entry) {
    return;
  }

  const handlersByEvent = entry.eventHandlers.get(eventName);
  if (!handlersByEvent) {
    return;
  }

  const handler = handlersByEvent.get(token);
  if (!handler) {
    return;
  }

  entry.channel.unbind(eventName, handler);
  handlersByEvent.delete(token);
  if (handlersByEvent.size === 0) {
    entry.eventHandlers.delete(eventName);
  }
};

export const clearChannelRegistry = (client: Pusher): void => {
  channelRegistry.forEach((entry, normalizedName) => {
    entry.eventHandlers.forEach((handlersByEvent, eventName) => {
      handlersByEvent.forEach(handler => {
        entry.channel.unbind(eventName, handler);
      });
    });
    client.unsubscribe(normalizedName);
  });

  channelRegistry.clear();
};
